import React, { Children } from 'react';
import cx from 'classnames';
import { mcdsFlagCheck } from '@mc/wink/helpers/utils-ts';
import Tooltip from '../Tooltip';
import { isValidLogo, getIntegrationEntryConfig } from '../../helpers/logo-map';
import stylesheet from './index.css';
import Coin, { type CoinProps } from './Coin';

export type CoinListProps = {
  label: string;
  children:
    | React.ReactElement<CoinProps, typeof Coin>
    | React.ReactElement<CoinProps, typeof Coin>[];
  tooltipDisabled?: boolean;
  direction?: 'top' | 'bottom' | 'left' | 'right';
};

const MAX_LOGOS_DISPLAYED = 4;

const CoinList: React.FC<CoinListProps> = ({
  label,
  children,
  tooltipDisabled = false,
  direction = 'bottom',
}) => {
  const isOverflowing = Children.count(children) > MAX_LOGOS_DISPLAYED;

  const isReleased = mcdsFlagCheck(
    'xp_mcds_redesign_components_organisms_part_2',
  );

  const labels = Children.map(children, (child) => {
    if (React.isValidElement(child) && child.type === Coin) {
      if ('label' in child.props) {
        return child.props.label;
      } else if (
        'imageKey' in child.props &&
        isValidLogo(child.props.imageKey)
      ) {
        return getIntegrationEntryConfig(child.props.imageKey).label;
      } else if (
        'fallbackImage' in child.props &&
        child.props.fallbackImage &&
        isValidLogo(child.props.fallbackImage)
      ) {
        return getIntegrationEntryConfig(child.props.fallbackImage).label;
      }
      return getIntegrationEntryConfig('mailchimp').label;
    }
    return null;
  })
    .filter((childLabel) => childLabel !== null)
    .join(', ');

  return (
    isReleased && (
      <Tooltip label={labels} disabled={tooltipDisabled} direction={direction}>
        <ul
          className={stylesheet.coinContainer}
          tabIndex={0}
          aria-label={label}
        >
          {!isOverflowing ? (
            children
          ) : (
            <>
              {Children.toArray(children).slice(0, MAX_LOGOS_DISPLAYED - 1)}
              <li className={cx(stylesheet.coin, stylesheet.overflow)}>
                {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
                <span className={stylesheet.overflowText}>
                  +{Children.count(children) - (MAX_LOGOS_DISPLAYED - 1)}
                </span>
              </li>
            </>
          )}
        </ul>
      </Tooltip>
    )
  );
};

export default CoinList;
